import { Component, inject, OnDestroy, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MatDialogActions, MatDialogContent, MatDialogRef, MatDialogTitle } from '@angular/material/dialog';
import { MatFormField } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { Subscription } from 'rxjs';
import { ReportDocumentTemplate } from '../../../api/app-types/report-document.app';
import { provideReportApi, REPORT_API } from '../../../api/services/report.service';

@Component({
	selector: 'lvr-template-update',
	standalone: true,
	imports: [
		ReactiveFormsModule,
		MatDialogTitle,
		MatDialogContent,
		MatDialogActions,
		MatFormField,
		MatSelectModule,
		MatInput,
		MatButton,
	],
	templateUrl: './template-update.html',
	styleUrl: './template-update.scss',
	providers: [provideReportApi],
})
export class LvrTemplateUpdate implements OnDestroy {
	private readonly reportApi = inject(REPORT_API);
	private readonly dialogRef = inject(MatDialogRef<LvrTemplateUpdate>);
	private readonly fb = inject(FormBuilder);
	private templateChangeSub?: Subscription;

	public readonly templates = signal<ReportDocumentTemplate[]>([]);
	public readonly loading = signal<boolean>(false);
	public readonly saving = signal<boolean>(false);
	public readonly form = this.fb.group({
		templateId: this.fb.control<number | null>(null, { validators: [Validators.required] }),
		html: this.fb.control('', { validators: [Validators.required] }),
	});

	public constructor() {
		this.templateChangeSub = this.form.controls.templateId.valueChanges.subscribe((id) => {
			const tpl = this.templates().find((t) => t.id === id);
			this.form.controls.html.setValue(tpl?.htmlContent ?? '');
		});
		this.loadTemplates();
	}

	public typeOfCareName(id?: number) {
		switch (id) {
			case 1:
				return 'Vermittlung';
			case 2:
				return 'Sicherung';
			default:
				return 'Unbekannt';
		}
	}

	public ngOnDestroy() {
		this.templateChangeSub?.unsubscribe();
	}

	private async loadTemplates() {
		this.loading.set(true);
		try {
			const res = await this.reportApi.getDocumentTemplates();
			this.templates.set(res.entities);
			const first = res.entities[0];
			if (first) {
				this.form.patchValue({
					templateId: first.id,
					html: first.htmlContent,
				});
			}
		} finally {
			this.loading.set(false);
		}
	}

	public async save() {
		if (this.form.invalid) return;

		const templateId = this.form.controls.templateId.value;
		const html = this.form.controls.html.value;
		if (!templateId) return;

		this.saving.set(true);
		try {
			await this.reportApi.updateReportTemplate(templateId, html ?? '');
			this.dialogRef.close(true);
		} catch (e) {
			console.error('Failed to update template', e);
		} finally {
			this.saving.set(false);
		}
	}

	public cancel() {
		this.dialogRef.close(false);
	}
}
